import React, { useEffect, useState, useMemo } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import "./VendorInfo.css";
import Table from "../Table/Table";
import NewRole from "../../Pages/NewRole/NewRole";
import NewEmployee from "../../Pages/NewEmployee";
import { useNavigate } from "react-router-dom";
import ApiManager from "../../ApiManager/ApiManager";
import { hasPermission, ShowButton } from "../RoleBasedComponents";
import PERMISSIONS from "../../permissions";
import { useAuth } from "../AuthProvider";
import { FaExclamationTriangle, FaExclamationCircle } from "react-icons/fa";
import { useLocation } from "react-router-dom";

const VendorInfo = () => {
  const { userPermissions, vendorDetails } = useAuth();

  const [isEditing, setIsEditing] = useState(false);
  const [editableVendorDetails, setEditableVendorDetails] =
    useState(vendorDetails);

  const [rowId, setRowId] = useState(null);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [alerts, setAlerts] = useState([]);

  const memoizedRoles = useMemo(() => roles, [roles]);

  const memoizedUsers = useMemo(() => users, [users]);

  const navigate = useNavigate();

  // useEffect(() => {
  //   fetchRoles();
  //   fetchUsers();
  // }, []);

  useEffect(() => {
    setEditableVendorDetails(vendorDetails);
    fetchAlerts();
  }, [vendorDetails]);

  const handleVendorInfoChange = () => {
    setIsEditing(true);
  };

  const getIconForStatus = (status) => {
    switch (status) {
      case "breached":
        return <FaExclamationTriangle className="alert-icon breached" />;
      case "warn":
        return <FaExclamationCircle className="alert-icon warn" />;
      default:
        return null;
    }
  };

  function formatText(text) {
    if (!text) return "";
    return text.replace(/_/g, " ").toUpperCase();
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (
      [
        "night_allowance_start",
        "night_allowance_end",
        "early_allowance_start",
        "early_allowance_end",
      ].includes(name)
    ) {
      setEditableVendorDetails((prev) => ({
        ...prev,
        [name]: `0001-01-01T${value}:00Z`, // Use "0001" instead of "0000" to avoid errors
      }));
    } else {
      setEditableVendorDetails((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const getUpdatedFields = () => {
    const updatedFields = {};

    Object.keys(editableVendorDetails).forEach((key) => {
      if (editableVendorDetails[key] !== vendorDetails[key]) {
        updatedFields[key] = editableVendorDetails[key];
      }
    });

    return updatedFields;
  };

  const handleSave = async () => {
    let updatedFields = getUpdatedFields();

    if (Object.keys(updatedFields).length === 0) {
      console.log("No changes detected.");
      return;
    }

    try {
      const query = new URLSearchParams({ id: vendorDetails.id });
      const response = await ApiManager.updateVendor(query, updatedFields);
      if (!response.ok) throw new Error("Failed to update vendor details");

      setIsEditing(false);
      alert("Vendor details updated successfully!");
    } catch (error) {
      console.error("Error updating vendor details:", error);
      alert("Failed to update vendor details.");
    }
  };

  const formatTime = (isoString) => {
    return isoString ? isoString.slice(11, 16) : "";
  };

  const formatDateTime = (isoString) => {
    return isoString ? isoString.slice(0, 16) : "";
  };

  const location = useLocation();

  useEffect(() => {
    if (location.pathname === "/login/home/roles") {
      fetchRoles();
    } else if (location.pathname.startsWith("/login/home/employees")) {
      fetchUsers();
    }
  }, [location.pathname]);

  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath === "/login/home") {
      navigate("/login/home/personal-info");
      setActiveItem("/login/home/personal-info");
    } else {
      setActiveItem(currentPath);
    }
  }, [navigate]);

  const fetchAlerts = async () => {
    try {
      const response = await ApiManager.getAlerts();
      if (!response.ok) throw new Error("Network response was not ok");
      const json = await response.json();
      console.log("res", json);
      setAlerts(json);
    } catch (error) {
      console.log("error", error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await ApiManager.getAllRoles();
      if (!response.ok) throw new Error("Network response was not ok");
      const json = await response.json();
      console.log("res", json);
      setRoles(json);
    } catch (error) {
      console.log("error", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await ApiManager.getAllUsers();
      if (!response.ok) throw new Error("Network response was not ok");
      const json = await response.json();
      console.log("res", json);
      setUsers(json);
    } catch (error) {
      console.log("error", error);
    }
  };

  const rolesColumns = [
    { field: "name", headerName: "Role" },
    { field: "description", headerName: "Description" },
  ];

  const rolesRows = useMemo(
    () =>
      (memoizedRoles || []).map((item) => ({
        id: item.id,
        name: item.name,
        description: item.description,
      })),
    [memoizedRoles]
  );

  const usersColumns = [
    { field: "username", headerName: "Username" },
    { field: "email", headerName: "Email" },
    { field: "role", headerName: "Role" },
  ];

  const usersRows = useMemo(
    () =>
      (memoizedUsers || []).map((item) => ({
        id: item.id,
        username: item.username,
        email: item.email,
        role: item.role.name,
      })),
    [memoizedUsers]
  );

  const items = [
    {
      label: "Personal Info",
      path: "/login/home/personal-info",
      activePath: "/login/home/personal-info",
    },
  ];

  if (hasPermission(userPermissions, [PERMISSIONS.VENDOR_ROLE_READ])) {
    items.push({
      label: "Roles",
      path: "/login/home/roles",
      activePath: "/login/home/roles",
    });
  }

  if (hasPermission(userPermissions, PERMISSIONS.VENDOR_USER_READ)) {
    items.push({
      label: "Employees",
      path: "/login/home/employees",
      activePath: "/login/home/employees",
    });
  }

  const [activeItem, setActiveItem] = useState("/login/home/personal-info");

  const handleClick = (label, path) => {
    setActiveItem(path);
    navigate(path);
    if (label === "Roles") fetchRoles();
    if (label === "Employees") fetchUsers();
  };

  return (
    <div className="vendor-container">
      {/* Welcome Header */}
      <header className="welcome-header">
        <div className="welcome-content">
          <h1>Welcome, {vendorDetails.name}</h1>
          <p>Manage your company information, roles, and employees</p>
        </div>
        <div>
          <ul className="top-navbar-menu">
            {items.map((item, index) => (
              <li
                key={index}
                className={`top-navbar-item ${
                  activeItem === item.activePath ? "active" : ""
                }`}
                onClick={() => handleClick(item.label, item.path)}
              >
                {item.label}
              </li>
            ))}
          </ul>
          {/* <h2>{heading(activeItem)}</h2> */}
        </div>
      </header>

      <main className="main-content">
        <Routes>
          <Route
            path="personal-info"
            element={
              <>
                <section className="section">
                  <div className="section-header">
                    <h2>Vendor Information</h2>
                    {/* {isEditing ? (
              <button className="save-button" onClick={handleSave}>
                Save
              </button>
            ) : (
              <button className="edit-button" onClick={handleVendorInfoChange}>
                Edit Information
              </button>
            )} */}
                  </div>
                  <div className="info-card">
                    <div className="info-grid">
                      {[
                        { label: "Company Name", key: "name" },
                        { label: "Email", key: "email" },
                        { label: "Phone", key: "phone" },
                        { label: "Address", key: "address" },
                        { label: "Billing Address", key: "billing_address" },
                        { label: "Place of Service", key: "place_of_service" },
                        { label: "GST Number", key: "gst_number" },
                      ].map(({ label, key }) => (
                        <div className="info-item" key={key}>
                          <label>{label}</label>
                          {isEditing ? (
                            <input
                              type="text"
                              name={key}
                              value={editableVendorDetails[key] || ""}
                              onChange={handleInputChange}
                            />
                          ) : (
                            <p>{vendorDetails[key]}</p>
                          )}
                        </div>
                      ))}

                      {/* Time Fields */}
                      {[
                        {
                          label: "Night Allowance Start",
                          key: "night_allowance_start",
                        },
                        {
                          label: "Night Allowance End",
                          key: "night_allowance_end",
                        },
                        {
                          label: "Early Allowance Start",
                          key: "early_allowance_start",
                        },
                        {
                          label: "Early Allowance End",
                          key: "early_allowance_end",
                        },
                      ].map(({ label, key }) => (
                        <div className="info-item" key={key}>
                          <label>{label}</label>
                          {isEditing ? (
                            <input
                              type="text"
                              name={key}
                              value={editableVendorDetails[key]}
                              onChange={handleInputChange}
                            />
                          ) : (
                            <p>{vendorDetails[key]}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </section>

                <div className="section-header">
                  <h2>Alerts ({alerts?.length || 0})</h2>
                </div>

                <div className="alerts-container">
                  {(alerts || []).map((alert) => (
                    <div key={alert.alert_for_id} className="alert-card">
                      <div className="alert-header">
                        <div className="alert-title">
                          <strong>
                            {alert.alert_for_name} ({alert.alert_for_type})
                          </strong>{" "}
                          ({formatText(alert.alert_type)}: {alert.expiry_time})
                        </div>
                        {/* <span className="alert-message">{alert.alert_message}</span> */}
                        <span className={`alert-status ${alert.alert_status}`}>
                          {getIconForStatus(alert.alert_status)}{" "}
                          {alert.alert_status}
                        </span>
                      </div>
                      {/* <div className="alert-message">{alert.alert_message}</div> */}
                      {/* <div className="alert-expiry">
                {formatText(alert.alert_type)}: {alert.expiry_time}
              </div> */}
                    </div>
                  ))}
                </div>
              </>
            }
          ></Route>

          <Route
            path="roles"
            element={
              <section className="section">
                <div className="section-header">
                  <h2>Roles</h2>
                  <ShowButton permission={PERMISSIONS.VENDOR_ROLE_CREATE}>
                    <NewRole />
                  </ShowButton>
                </div>
                <div className="table-container">
                  <Table 
                    table_columns={rolesColumns}
                    view_columns={rolesColumns}
                    rows={rolesRows}
                    setRowsId={setRowId}
                  />
                </div>
              </section>
            }
          ></Route>

          <Route
            path="employees/*"
            element={
              <section className="section">
                <div className="section-header">
                  <h2>Employees</h2>
                  <ShowButton permission={[PERMISSIONS.VENDOR_USER_CREATE]}>
                    <NewEmployee />
                  </ShowButton>
                </div>
                <div className="table-container">
                  <Table
                    table_columns={usersColumns}
                    view_columns={usersColumns}
                    rows={usersRows}
                    setRowsId={setRowId}
                  />
                </div>
              </section>
            }
          ></Route>
        </Routes>
      </main>
    </div>
  );
};

export default VendorInfo;
